import axios from 'axios';

import {
    AskStreamCustomInstructions,
    CaseInfo,
    ChatHistorySessionPayload,
    ChatHistorySessionPayloadByModels,
    DisclaimerTriggerBy,
    FeedbackType,
    FeedbackValue,
    GenerationType,
    HighlightSuggestionPayload
} from '../types';

const permanentUrl =
    window.__triageStore.scopServerBaseUrl ||
    (process.env.REACT_APP_API_SERVER as string);
const tempBaseUrl = process.env.REACT_APP_TEMP_API_SERVER as string;
const baseUrl = window?.__triageStore?.is_using_temp_server
    ? tempBaseUrl
    : permanentUrl;

// Prefixes
const askApiPrefix = baseUrl + '/ask';
const adminApiPrefix = baseUrl + '/admin';

// URIs
const askUrl = askApiPrefix + '/ask';
const sourceUrl = askApiPrefix + '/source_url';
const askStream = askApiPrefix + '/ask_stream';
const searchUrl = askApiPrefix + '/search';
const feedbackUrl = askApiPrefix + '/feedback';
const stateCheckUrl = askApiPrefix + '/state';
const observationSuggestions = askApiPrefix + '/observation/suggestions';
const highlightSuggestions = askApiPrefix + '/highlight/suggestions';
const chatUrl = askApiPrefix + '/chat';
const feedbackCategoriesUrl = askApiPrefix + '/feedback_categories';
const questionPresetsUrl = askApiPrefix + '/question_presets';
const chatHistorySessionUrl = askApiPrefix + '/chat_history_session';
const chatArchiveUrl = askApiPrefix + '/archive_chat';
const suggestionQuestions = askApiPrefix + '/suggestions/search';
const updateResponseByExpert = askApiPrefix + '/update_response_by_expert';
const chatSummary = askApiPrefix + '/chat/summary';
const rangedModel = askApiPrefix + '/ranged_model';
const chatHistorySessionByModelsUrl =
    askApiPrefix + '/chat_history_grouped_by_models';

const metadataUrl = adminApiPrefix + '/tenant/metadata';
const disclaimerList = adminApiPrefix + '/disclaimer/list';

enum ScopInstanceTypeEnum {
    PRODUCTION = 'Production',
    SANDBOX = 'Sandbox'
}

class Backend {
    private static async getCommonHeaders() {
        return {
            Authorization: `Bearer ${window.__sfdcIds.sessionId}`,
            'X-Scop-Client-Type': 'Salesforce',
            'X-Scop-Instance-Type': window.__triageStore.isSandboxOrg
                ? ScopInstanceTypeEnum.SANDBOX
                : ScopInstanceTypeEnum.PRODUCTION
        };
    }

    public static async ask(
        model_name: string,
        question: string,
        return_all_results: boolean,
        chat_id: string
    ) {
        const req = { question, chat_id, return_all_results };
        return axios.post(`${askUrl}/${model_name}`, req, {
            headers: await Backend.getCommonHeaders()
        });
    }

    public static async getSourceUrl(
        model_name: string,
        file_name: string,
        page_index: number
    ) {
        const req = { model_name, file_name, page_index };
        return axios.post(sourceUrl, req, {
            headers: await Backend.getCommonHeaders()
        });
    }

    public static async askStream(
        model_name: string,
        question: string,
        return_all_results: boolean,
        chat_id: string,
        regenerate: boolean,
        previous_generation_type: GenerationType | null,
        skip_question_pp: boolean = false,
        signal: AbortSignal,
        triage_product_id: string,
        case_info: CaseInfo,
        custom_instructions: AskStreamCustomInstructions,
        prevent_history_save: boolean = false,
        model_serial_number?: string
    ) {
        const req = {
            question,
            chat_id,
            return_all_results,
            regenerate,
            previous_generation_type,
            skip_question_pp,
            triage_product_id,
            case_info,
            custom_instructions,
            prevent_history_save,
            model_serial_number
        };

        return fetch(`${askStream}/${model_name}`, {
            method: 'POST',
            headers: {
                ...(await Backend.getCommonHeaders()),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(req),
            signal
        });
    }

    public static async search(model_name: string, question: string) {
        const req = { question };

        return axios.post(`${searchUrl}/${model_name}`, req, {
            headers: await Backend.getCommonHeaders()
        });
    }

    public static async sendFeedback(
        modelName: string,
        feedbackId: string,
        question: string,
        answer: string,
        isUseful: boolean,
        categories: Array<string>,
        feedbackText: string,
        sourceDocument: any,
        messageRowId: string,
        isFirstQuestion: boolean,
        feedbackType: FeedbackType,
        feedbackValue: FeedbackValue
    ) {
        const req = {
            feedback_id: feedbackId,
            question,
            answer,
            is_useful: isUseful,
            categories,
            feedback_text: feedbackText,
            current_source: sourceDocument,
            chat_history_line_id: messageRowId,
            is_first_question: isFirstQuestion,
            feedback_type: feedbackType,
            feedback_value: feedbackValue
        };

        return axios.post(`${feedbackUrl}/${modelName}`, req, {
            headers: await Backend.getCommonHeaders()
        });
    }

    public static async performStateCheck(model_name: string) {
        const req = {};

        return axios.post(`${stateCheckUrl}/${model_name}`, req, {
            headers: await Backend.getCommonHeaders()
        });
    }

    public static async fetchMetadata(modelsByPersona: boolean = false) {
        const config = {
            headers: await Backend.getCommonHeaders(),
            params: {
                filter_by_user_persona: modelsByPersona
            }
        };
        return axios.get(`${metadataUrl}`, config);
    }

    public static async observationSuggestions(
        model_name: string,
        observation: string
    ) {
        const req = {
            observation
        };
        return axios.post(`${observationSuggestions}/${model_name}`, req, {
            headers: await Backend.getCommonHeaders()
        });
    }

    public static async highlightSuggestions(
        model_name: string,
        payload: HighlightSuggestionPayload
    ) {
        return axios.post(`${highlightSuggestions}/${model_name}`, payload, {
            headers: await Backend.getCommonHeaders()
        });
    }

    public static async getChatHistory(chatId: string) {
        return axios.get(`${chatUrl}/${chatId}`, {
            headers: await Backend.getCommonHeaders()
        });
    }
    public static async getFeedbackCategories(locale: string) {
        const params = { locale, return_defaults: true };
        return axios.get(feedbackCategoriesUrl, {
            headers: await Backend.getCommonHeaders(),
            params
        });
    }

    public static async getQuestionPresets(locale: string) {
        const params = { locale, return_defaults: true };
        return axios.get(questionPresetsUrl, {
            headers: await Backend.getCommonHeaders(),
            params
        });
    }

    public static async getChatHistorySession(
        payload: ChatHistorySessionPayload
    ) {
        return axios.get(
            chatHistorySessionUrl +
                `?tenant_model=${payload.tenant_model}` +
                `&search_term=${payload.search_term}` +
                `&is_descending=${payload.is_descending}` +
                `&offset=${payload.offset}` +
                `&limit=${payload.limit}`,
            {
                headers: await Backend.getCommonHeaders()
            }
        );
    }

    public static async getChatHistorySessionByModels(
        payload: ChatHistorySessionPayloadByModels
    ) {
        return axios.get(
            chatHistorySessionByModelsUrl +
                `?search_term=${payload.search_term}` +
                `&offset=${payload.offset}` +
                `&limit=${payload.limit}` +
                `&limit_per_model=${payload.limit_per_model}`,
            {
                headers: await Backend.getCommonHeaders()
            }
        );
    }

    public static async archiveChat(chat_id: string) {
        const req = {
            chat_id
        };
        return axios.post(`${chatArchiveUrl}`, req, {
            headers: await Backend.getCommonHeaders()
        });
    }
    public static async getSuggestionQuestions(
        query: string,
        model_name?: string
    ) {
        return axios.get(suggestionQuestions, {
            headers: await Backend.getCommonHeaders(),
            params: {
                query,
                model_name
            }
        });
    }

    public static async updateResponseByExpert(
        chat_id: string,
        chat_history_line_id: string,
        tenant_model: string,
        question: string,
        condensed_question: string,
        edited_response: string,
        triage_product_id: string,
        user_name: string
    ) {
        const req = {
            chat_id,
            chat_history_line_id,
            tenant_model,
            question,
            condensed_question,
            edited_response,
            triage_product_id,
            user_name
        };
        return axios.post(`${updateResponseByExpert}`, req, {
            headers: await Backend.getCommonHeaders()
        });
    }

    public static async fetchMessageDisclaimers(
        locale: string,
        question?: string,
        answer?: string,
        disclaimer_trigger_by?: DisclaimerTriggerBy,
        is_active: boolean = true
    ) {
        const payload = {
            locale,
            is_active,
            question,
            answer,
            disclaimer_trigger_by
        };
        const response = await axios.post(disclaimerList, payload, {
            headers: await Backend.getCommonHeaders()
        });
        return response.data;
    }

    public static async updateChatSummary(
        question: string,
        modelName: string,
        chatId: string
    ) {
        const req = {
            question,
            model_name: modelName,
            chat_id: chatId
        };

        const response = await axios.patch(chatSummary, req, {
            headers: await Backend.getCommonHeaders()
        });
        return response.data;
    }

    public static async fetchRangedModel(serialNumber: string) {
        const req = {
            input: serialNumber
        };

        const response = await axios.post(rangedModel, req, {
            headers: await Backend.getCommonHeaders()
        });
        return response.data;
    }
}

export default Backend;
